// src/components/winback/WinbackKPIs.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaperPlane,
    faCheckCircle,
    faChartLine,
    faEnvelope,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useWinbackStats } from '../../hooks/useWinbackStats';

const KPICard = ({ title, value, icon, color = 'blue', loading, tooltip }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const colorClasses = {
        blue: 'from-blue-500 to-blue-600',
        green: 'from-green-500 to-green-600',
        purple: 'from-purple-500 to-purple-600',
        indigo: 'from-indigo-500 to-indigo-600'
    };

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 relative">
            <div className="flex items-center">
                <div className={`w-12 h-12 bg-gradient-to-r ${colorClasses[color]} rounded-lg flex items-center justify-center`}>
                    <FontAwesomeIcon icon={icon} className="text-white text-xl" />
                </div>
                <div className="ml-4 flex-1">
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-600">{title}</p>
                        {tooltip && (
                            <div className="relative">
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="text-gray-400 text-xs cursor-help"
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                />
                                {showTooltip && (
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50">
                                        {tooltip}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                            <div className="w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {loading ? (
                        <div className="h-8 w-20 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                        <p className="text-2xl font-bold text-gray-900">{value}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const WinbackKPIs = ({ marcaId }) => {
    const { stats, loading } = useWinbackStats(marcaId);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <KPICard
                title="Total Campañas"
                value={stats?.totalCampaigns || 0}
                icon={faEnvelope}
                color="blue"
                loading={loading}
            />
            <KPICard
                title="Enviados"
                value={stats?.sent || 0}
                icon={faPaperPlane}
                color="indigo"
                loading={loading}
            />
            <KPICard
                title="Convertidos"
                value={stats?.converted || 0}
                icon={faCheckCircle}
                color="green"
                loading={loading}
                tooltip="Un cliente se considera 'convertido' cuando vuelve a consumir en los 30 días posteriores al envío del WinBack. Esto se detecta automáticamente cuando registras una nueva visita o compra."
            />
            <KPICard
                title="Tasa de Conversión"
                value={stats?.conversionRate ? `${stats.conversionRate.toFixed(2)}%` : '0%'}
                icon={faChartLine}
                color="purple"
                loading={loading}
                tooltip="Porcentaje de clientes que recibieron un WinBack y volvieron a consumir en los 30 días siguientes. Calculado como: (Convertidos / Enviados) × 100"
            />
        </div>
    );
};

export default WinbackKPIs;

